import {useEffect, useRef, useState} from "react";

import {Client} from "../../Client.ts";
import RelativeLoadingFrame from "../../../../../components/RelativeLoadingFrame.tsx";
import {InfoTooltip} from "../../../../../components/general/InfoTooltip.tsx";
import { useNotifications } from "../../../../../lib/NotificationContext.tsx";


export interface Settings {
    emailSeparationEnabled: boolean;
    salesInvoiceEmailAddress: string | null;
    purchaseInvoiceEmailAddress: string | null;
    otherDocumentsEmailAddress: string | null;
    missingDocumentsStartDate: string | null;
    bookkeepingStartDate: string | null;
    businessDescription: string;
    bookingSuggestions: string;
}

export const MAX_BOOKING_CONTEXT_LENGTH = 2048;

function rejectionMessageOf(error: unknown): string | null {
    const detail = (error as { detail?: unknown })?.detail;

    return typeof detail === 'string' && detail.length > 0 ? detail : null;
}

export type StoredSettings = Omit<Settings, 'businessDescription'>;

export interface ClientSettingsProps {
    client: Client;
    onSave: (settings: Settings) => Promise<void>;
    getAllSettingsForBusiness: (businessId: string) => Promise<StoredSettings>;
}

function calculateNewSaveState(data: {
    emailSeparationEnabledInput: boolean;
    salesInvoiceEmailAddressInput: string | null;
    purchaseInvoiceEmailAddressInput: string | null;
    otherDocumentsEmailAddressInput: string | null;
    generalDocumentsEmailInput: string | null;
}): {
    emailSeparationEnabled: boolean;
    salesInvoiceEmailAddress: string | null;
    purchaseInvoiceEmailAddress: string | null;
    otherDocumentsEmailAddress: string | null;
} {
    const normalizeEmail = (value: string | null): string | null => {
        const v = (value ?? '').trim();
        return v.length ? v : null;
    };
    if (data.emailSeparationEnabledInput)
        return {
            emailSeparationEnabled: true,
            salesInvoiceEmailAddress: normalizeEmail(data.salesInvoiceEmailAddressInput),
            purchaseInvoiceEmailAddress: normalizeEmail(data.purchaseInvoiceEmailAddressInput),
            otherDocumentsEmailAddress: normalizeEmail(data.otherDocumentsEmailAddressInput)
        }
    return {
        emailSeparationEnabled: false,
        salesInvoiceEmailAddress: normalizeEmail(data.generalDocumentsEmailInput),
        purchaseInvoiceEmailAddress: normalizeEmail(data.generalDocumentsEmailInput),
        otherDocumentsEmailAddress: normalizeEmail(data.generalDocumentsEmailInput)
    }
}

export default function ClientSettings({
                                           client,
                                           onSave,
                                           getAllSettingsForBusiness,
                                       }: ClientSettingsProps) {
    const salesInvoiceEmailInput = useRef<HTMLInputElement>(null);
    const purchaseInvoiceEmailInput = useRef<HTMLInputElement>(null);
    const otherDocumentsEmailInput = useRef<HTMLInputElement>(null);
    const generalDocumentsEmailInput = useRef<HTMLInputElement>(null);
    const missingDocumentsStartDate = useRef<HTMLInputElement>(null);
    const bookkeepingStartDateRef = useRef<HTMLInputElement>(null);
    const businessDescriptionInput = useRef<HTMLTextAreaElement>(null);
    const bookingSuggestionsInput = useRef<HTMLTextAreaElement>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [missingDocumentsStartDateValue, setMissingDocumentsStartDateValue] = useState<string | null>(null);
    const [bookkeepingStartDateValue, setBookkeepingStartDateValue] = useState<string | null>(null);
    const {dispatch} = useNotifications();

    const [settings, setSettings] = useState<Settings>({
        emailSeparationEnabled: false,
        salesInvoiceEmailAddress: null,
        purchaseInvoiceEmailAddress: null,
        otherDocumentsEmailAddress: null,
        missingDocumentsStartDate: null,
        bookkeepingStartDate: null,
        businessDescription: client.description ?? '',
        bookingSuggestions: '',
    });
    const [rejectionMessage, setRejectionMessage] = useState<string | null>(null);
    const [isSeparateEmailsEnabled, setIsSeparateEmailsEnabled] = useState<boolean>(settings.emailSeparationEnabled || false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const loadSettings = async () => {
            try {
                const savedSettings = await getAllSettingsForBusiness(client.id);
                setSettings({...savedSettings, businessDescription: client.description ?? ''});
                setIsSeparateEmailsEnabled(savedSettings.emailSeparationEnabled);
                setMissingDocumentsStartDateValue(savedSettings.missingDocumentsStartDate ?? null);
                setBookkeepingStartDateValue(savedSettings.bookkeepingStartDate ?? null);
            } catch (error) {
                console.error("Failed to load settings:", error);
            } finally {
                setIsLoading(false);
            }
        };

        loadSettings();
    }, [client.id, client.description, getAllSettingsForBusiness]);

    useEffect(() => {
        if (salesInvoiceEmailInput.current) {
            salesInvoiceEmailInput.current.value = settings.salesInvoiceEmailAddress || '';
        }
        if (purchaseInvoiceEmailInput.current) {
            purchaseInvoiceEmailInput.current.value = settings.purchaseInvoiceEmailAddress || '';
        }
        if (otherDocumentsEmailInput.current) {
            otherDocumentsEmailInput.current.value = settings.otherDocumentsEmailAddress || '';
        }
        if (generalDocumentsEmailInput.current) {
            generalDocumentsEmailInput.current.value = settings.otherDocumentsEmailAddress || '';
        }
        if (missingDocumentsStartDate.current) {
            missingDocumentsStartDate.current.value = settings.missingDocumentsStartDate || '';
        }
        if (bookkeepingStartDateRef.current) {
            bookkeepingStartDateRef.current.value = settings.bookkeepingStartDate || '';
        }
        if (businessDescriptionInput.current) {
            businessDescriptionInput.current.value = settings.businessDescription || '';
        }
        if (bookingSuggestionsInput.current) {
            bookingSuggestionsInput.current.value = settings.bookingSuggestions || '';
        }

    }, [settings]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const form = e.currentTarget;
        if (!form.checkValidity()) {
            form.reportValidity();
            return;
        }

        setIsSaving(true);
        setRejectionMessage(null);
        try {
            const base = calculateNewSaveState({
                emailSeparationEnabledInput: isSeparateEmailsEnabled,
                salesInvoiceEmailAddressInput: salesInvoiceEmailInput.current?.value || "",
                purchaseInvoiceEmailAddressInput: purchaseInvoiceEmailInput.current?.value || "",
                otherDocumentsEmailAddressInput: otherDocumentsEmailInput.current?.value || "",
                generalDocumentsEmailInput: generalDocumentsEmailInput.current?.value || ""
            });
            const normalizeDate = (value: string | null | undefined): string | null => {
                const v = (value ?? '').trim();
                return v.length ? v : null;
            }
            const payload = {
                ...base,
                missingDocumentsStartDate: normalizeDate(missingDocumentsStartDate.current?.value),
                bookkeepingStartDate: normalizeDate(bookkeepingStartDateRef.current?.value),
                businessDescription: (businessDescriptionInput.current?.value ?? '').trim(),
                bookingSuggestions: (bookingSuggestionsInput.current?.value ?? '').trim()
            } as Settings;
            await onSave(payload);
            dispatch({
                title: 'Succes',
                description: 'Instellingen succesvol opgeslagen.',
                type: 'success',
            });
        } catch (error) {
            console.error(error);
            const rejection = rejectionMessageOf(error);

            if (rejection) {
                setRejectionMessage(rejection);
                dispatch({
                    title: 'Niet opgeslagen',
                    description: rejection,
                    type: 'error',
                });
            } else {
                dispatch({
                    title: 'Fout',
                    description: 'Er ging iets mis bij het opslaan van de instellingen.',
                    type: 'error',
                });
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
          
            <form onSubmit={handleSubmit} className="flex flex-col gap-6 w-full">
                <div>
                    <h3 className="border-b border-gray-200 pb-2 text-lg font-semibold text-gray-900">
                        Documenten doorsturen
                    </h3>
                    <p className="mt-3 text-sm text-gray-500">
                        Documenten die je klanten delen via PocketCFO, worden automatisch naar de volgende
                        e-mailadres(sen) doorgestuurd:
                    </p>
                </div>
                <RelativeLoadingFrame isLoading={isLoading}>
                    <div className="space-y-6">
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setIsSeparateEmailsEnabled((prev: boolean) => (!prev))}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                    isSeparateEmailsEnabled ? 'bg-primary' : 'bg-gray-200'
                                }`}
                            >
                            <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                    isSeparateEmailsEnabled ? 'translate-x-5' : 'translate-x-0'
                                }`}
                            />
                            </button>
                            <span className="text-sm text-gray-600">
                            Toon verschillende e-mailadressen voor inkoop en verkoopfacturen.
                        </span>
                        </div>
                        {isSeparateEmailsEnabled ? (
                            <>
                                <div className="flex flex-col gap-2">
                                    <label htmlFor="sales-invoice-email"
                                           className="block text-sm font-semibold text-gray-900">
                                        E-mailadres voor verkoopfacturen
                                        {isSeparateEmailsEnabled && (
                                            <InfoTooltip
                                                className="ml-1"
                                                text="Verplicht wanneer e-mailscheiding is ingeschakeld"
                                                position="right"
                                            />
                                        )}
                                    </label>

                                    <input
                                        id="sales-invoice-email"
                                        type="email"
                                        ref={salesInvoiceEmailInput}
                                        defaultValue={settings.salesInvoiceEmailAddress || ''}
                                        className="block w-full max-w-md rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:outline-none focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                        placeholder="verkoop@example.com"
                                        required={isSeparateEmailsEnabled}
                                    />
                                </div>

                                <div className="flex flex-col gap-2">
                                    <label htmlFor="purchase-invoice-email"
                                           className="block text-sm font-semibold text-gray-900">
                                        E-mailadres voor inkoopfacturen
                                        {isSeparateEmailsEnabled && (
                                            <InfoTooltip
                                                className="ml-1"
                                                text="Verplicht wanneer e-mailscheiding is ingeschakeld"
                                                position="right"
                                            />
                                        )}
                                    </label>

                                    <input
                                        id="purchase-invoice-email"
                                        type="email"
                                        ref={purchaseInvoiceEmailInput}
                                        defaultValue={settings.purchaseInvoiceEmailAddress || ''}
                                        className="block w-full max-w-md rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:outline-none focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                        placeholder="inkoop@example.com"
                                        required={isSeparateEmailsEnabled}
                                    />
                                </div>

                                <div className="flex flex-col gap-2">
                                    <label htmlFor="other-documents-email"
                                           className="block text-sm font-semibold text-gray-900">
                                        E-mailadres voor overige documenten
                                        {isSeparateEmailsEnabled && (
                                            <InfoTooltip
                                                className="ml-1"
                                                text="Verplicht wanneer e-mailscheiding is ingeschakeld"
                                                position="right"
                                            />
                                        )}
                                    </label>

                                    <input
                                        id="other-documents-email"
                                        type="email"
                                        ref={otherDocumentsEmailInput}
                                        defaultValue={settings.otherDocumentsEmailAddress || ''}
                                        className="block w-full max-w-md rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:outline-none focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                        placeholder="algemeen@example.com"
                                        required={isSeparateEmailsEnabled}
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col gap-2">
                                <label htmlFor="general-documents-email"
                                       className="block text-sm font-semibold text-gray-900">
                                    E-mailadres voor alle documenten
                                </label>
                                <input
                                    id="general-documents-email"
                                    type="email"
                                    ref={generalDocumentsEmailInput}
                                    defaultValue={settings.otherDocumentsEmailAddress || ''}
                                    className="block w-full max-w-md rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    placeholder="algemeen@example.com"
                                />
                            </div>
                        )}

                        <div className="pt-2 space-y-5">
                            <h3 className="border-b border-gray-200 pb-2 text-lg font-semibold text-gray-900">
                                Boekingscontext
                            </h3>

                            <p className="text-sm text-gray-500">
                                Met deze twee velden vertel je de automatische boekhouding hoe deze klant in
                                elkaar zit. In de omschrijving leg je uit wat het bedrijf doet. Bij Boeking
                                suggesties geef je concrete boekingsregels, bijvoorbeeld: &quot;Boek inkopen bij
                                Sligro altijd als Inkoop, omdat dit bedrijf een horecazaak is.&quot; Beide teksten
                                worden meegegeven aan de AI bij het boeken van documenten en banktransacties.
                                Wijzigingen gelden alleen voor nieuwe boekingen: al verwerkte documenten en
                                transacties worden niet opnieuw geboekt.
                            </p>

                            {rejectionMessage && (
                                <div
                                    role="alert"
                                    className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
                                >
                                    <span className="font-semibold">Tekst niet opgeslagen. </span>
                                    {rejectionMessage}
                                </div>
                            )}

                            <div className="flex flex-col gap-2">
                                <label
                                    htmlFor="business-description"
                                    className="block text-sm font-semibold text-gray-900"
                                >
                                    Omschrijving van het bedrijf
                                </label>
                                <textarea
                                    id="business-description"
                                    ref={businessDescriptionInput}
                                    defaultValue={settings.businessDescription}
                                    rows={3}
                                    maxLength={MAX_BOOKING_CONTEXT_LENGTH}
                                    className="block w-full max-w-md rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:outline-none focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    placeholder="Bijvoorbeeld: horecazaak met twee vestigingen in Utrecht."
                                />
                            </div>

                            <div className="flex flex-col gap-2">
                                <label
                                    htmlFor="booking-suggestions"
                                    className="block text-sm font-semibold text-gray-900"
                                >
                                    Boeking suggesties
                                </label>
                                <textarea
                                    id="booking-suggestions"
                                    ref={bookingSuggestionsInput}
                                    defaultValue={settings.bookingSuggestions}
                                    rows={5}
                                    maxLength={MAX_BOOKING_CONTEXT_LENGTH}
                                    className="block w-full max-w-md rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:outline-none focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    placeholder="Bijvoorbeeld: boek inkopen bij Sligro altijd als Inkoop, omdat dit bedrijf een horecazaak is."
                                />
                            </div>
                        </div>

                        <div className="pt-2 space-y-5">
                            <h3 className="border-b border-gray-200 pb-2 text-lg font-semibold text-gray-900">
                                Boekhouden
                            </h3>

                            <div className="flex flex-col gap-2">
                                <label
                                    htmlFor="vraagposten-start-date"
                                    className="block text-sm font-semibold text-gray-900"
                                >
                                    Vraagposten startdatum
                                </label>
                                <p className="text-sm text-gray-500">
                                    Vraagposten na de gekozen datum worden aan je klanten getoond. Alleen transacties vanaf deze datum worden meegenomen.
                                </p>
                                <input
                                    id="vraagposten-start-date"
                                    type="date"
                                    ref={missingDocumentsStartDate}
                                    defaultValue={missingDocumentsStartDateValue || ''}
                                    className="block w-full max-w-md rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:outline-none focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                />
                            </div>

                            <div className="flex flex-col gap-2">
                                <label
                                    htmlFor="bookkeeping-start-date"
                                    className="block text-sm font-semibold text-gray-900"
                                >
                                    Boekhouding startdatum
                                </label>
                                <p className="text-sm text-gray-500">
                                    Hiermee geef je aan vanaf welke datum je de boekhouding voert; de balans (en afgeleide totalen) in de Kolommen Balans wordt dan alleen opgebouwd vanaf die datum. Tip: als je de Boekhouding startdatum instelt op bijv. 1 jan 2026, maak dan een openingsbalans (memoriaal) boeking met datum 1 jan 2026. Automatisch matchen staat uit tot de startdatum is ingesteld.
                                </p>
                                <input
                                    id="bookkeeping-start-date"
                                    type="date"
                                    ref={bookkeepingStartDateRef}
                                    defaultValue={bookkeepingStartDateValue || ''}
                                    className="block w-full max-w-md rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:outline-none focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                />
                            </div>
                        </div>
                    </div>
                </RelativeLoadingFrame>

                <div className="mt-6 flex justify-end">
                    <button
                        type="submit"
                        disabled={isSaving}
                        className={`relative flex items-center leading-0 rounded-md px-3.5 py-1.5 shadow-sm transition hover:transition focus:outline-none focus:ring-offset-2 ${
                            isSaving
                                ? 'bg-gray-200 text-gray-600 cursor-not-allowed'
                                : 'bg-primary text-white hover:bg-darkprimary focus:ring-2 focus:ring-primary'
                        }`}
                    >
                        {isSaving ? "Bezig met opslaan..." : "Instellingen opslaan"}
                    </button>
                </div>
            </form>
        </>
    );
}
